import React, { useCallback, useMemo } from "react";

import { faLink } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik, FormikTouched, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { MultiButton } from "components/Button";
import { DeploymentBusinessForm } from "components/Business/BusinessForm/DeploymentBusinessForm";
import { TemporaryBusinessMessage } from "components/Business/BusinessForm/TemporaryBusinessMessage";
import { AddButton as SyncButton } from "components/Button/CollapseInlineButton";
import { Button, SecondaryButton } from "components/Form";
import { mapIncomingTerms } from "components/Form/FormikControls/TradingTerms/utils";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { AuditLogTypes } from "constants/auditLog";
import { ApiModel } from "constants/loading";
import { DeploymentPermission } from "constants/permissions";
import { Settings } from "constants/settings";

import { getBusinessSubType } from "lib/businesses";
import {
  mapBusinessValuesToInsuranceType,
  mapDefaultDeploymentTransitInsuranceToBusinessValues,
} from "lib/insuranceRates";
import { getSelectSyncDataHashRoute } from "lib/navigation";

import {
  getActiveLivestockAgentDeployment,
  getActiveSaleyardAdminSaleyard,
  getBusinessRelationsByBusinessId,
  getBusinessTradingTermsById,
  getContextByModalType,
  getDefaultBillingSettings,
  getDefaultBuyerSplitByBusinessId,
  getDefaultVendorSplitByBusinessId,
  getInterestSettingsByBusinessId,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getPropertyEnrichedBusinessByBusinessId,
  getVendorCommissionBandsByBusinessId,
  selectRoleDeployments,
} from "selectors";

import {
  useHasDefaultBuyerSplitPermission,
  useHasDefaultVendorSplitPermission,
  useHasDeploymentPermission,
  useSomeHasPermission,
} from "hooks/useHasPermission";
import { useBusinessFormSync } from "hooks/useBusinessFormSync";

import noop from "lodash/noop";

import {
  computeInitialValues,
  ModalContextWithSnapshot,
} from "sagas/modals/util";
import { ModalTypes } from "constants/navigation";
import { isEmpty } from "lodash";
import { submitBusinessForm } from "actions/modals/business";

import { getBusinessValidationSchema } from "./validation";
import { BusinessFormValues, DefaultSplit } from "./types";

interface BusinessFormProps {
  closeSelf: () => void;
  business: Business | null;
  section?: string;
  handleSubmit?: (
    values: BusinessFormValues,
    submittedTouched: FormikTouched<BusinessFormValues>,
    closeAfterSave?: boolean,
  ) => void;
  defaultAddressSearch?: string;
  // Note this gets propdrilled with a {...props}
  openSideLoadedBusinessModal?: boolean;
  sideLoadedBusinessId?: string;
}

interface EditBusinessProps {
  onClose: () => void;
}

const blankDefaults: Partial<BusinessFormValues> = {
  address: {},
  bankingDetails: null,
  businessUsers: [],
  buyerWays: [],
  emailRecipients: [],
  generateBpayCRN: true,
  isActive: true,
  isBuyer: false,
  isTopBuyerCattle: false,
  isTopBuyerSheep: false,
  isTransporter: false,
  isVendor: false,
  properties: [],
  relationships: [],
  shortCodeSaleyard: "",
  shortCode: "",
  shortCodeAuctionsPlus: "",
  vendorCommissionBands: [],
  defaultBuyerSplit: undefined,
  defaultVendorSplit: undefined,
  interestSettings: [],
  labelIds: [],
};

const useBuildInitialValues = (
  /**
   * Returns both the effective initialValues (including restored from modal context snapshot)
   * and a "baseline" version with the snapshot stripped, used to detect
   * whether a restored form differs from the original business state.
   */
  context: Partial<ModalContext>,
  hasVendorCommissionsFeature: boolean,
  hasDefaultVendorSplitsFeature: boolean,
  hasDefaultBuyerSplitsFeature: boolean,
  hasRelationshipsFeature: boolean,
  business?: Business,
) => {
  const vendorCommissionBandsRaw: VendorCommissionBand[] = useSelector(
    getVendorCommissionBandsByBusinessId(business?.id),
  );

  const defaultVendorSplit: DefaultSplit = useSelector(
    getDefaultVendorSplitByBusinessId(business?.id),
  );

  const defaultBuyerSplit: DefaultSplit = useSelector(
    getDefaultBuyerSplitByBusinessId(business?.id),
  );

  const interestSettingsRaw: InterestSettings[] = useSelector(
    getInterestSettingsByBusinessId(business?.id),
  );

  const relationshipsRaw: BusinessRelation[] = useSelector(
    getBusinessRelationsByBusinessId(business?.id),
  );

  const tradingTermsLookup = useSelector(getBusinessTradingTermsById);

  const billingSettings = useSelector(getDefaultBillingSettings);

  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermission.featureInterest,
  );

  // TODO: to be migration into a helper function passed into computeInitialValues
  const applyDerivedValues = useCallback(
    (target: Partial<BusinessFormValues>) => {
      // Vendor commission bands
      if (hasVendorCommissionsFeature) {
        target.vendorCommissionBands = vendorCommissionBandsRaw;
      }

      // Default splits
      if (hasDefaultVendorSplitsFeature) {
        target.defaultVendorSplit = defaultVendorSplit || undefined;
      }
      if (hasDefaultBuyerSplitsFeature) {
        target.defaultBuyerSplit = defaultBuyerSplit || undefined;
      }

      // Interest
      if (hasInterestFeature) {
        const interestSettings = interestSettingsRaw ?? [];

        if (interestSettings.length === 0) {
          target.interestSettings = [
            {
              debitInterestRate: 0,
              creditInterestRate: 0,
              daysOverdueThreshold: 0,
              id: uuidv4(),
            },
          ];
        } else {
          target.interestSettings = interestSettings;
        }
      }

      // Relationships
      if (hasRelationshipsFeature) {
        target.relationships = relationshipsRaw ?? [];
      } else {
        // As the API may still be backward compatibily sending the relationships down in the
        // business payload, and we don't want to send it back unless we're certain - if I don't
        // have the permission make sure I don't throw it back.
        target.relationships = undefined;
      }

      // Trading terms
      const tradingTerms = (tradingTermsLookup?.[business?.id] ?? []).map(
        mapIncomingTerms,
      );

      target.tradingTerms = tradingTerms;

      // Billing / transit insurance mapping
      // If we have a default BillingSettings, use them for the transit insurance default
      if (billingSettings) {
        const transitInsuranceDefault =
          billingSettings.transitInsuranceApplicationDefault;

        // If we have no business we are creating one,
        // so set the default from the deployment, and map it into initial values
        if (!business) {
          target.transitInsuranceApplicationDefault = transitInsuranceDefault;

          const transitInsuranceRates =
            mapDefaultDeploymentTransitInsuranceToBusinessValues(
              transitInsuranceDefault,
            );

          Object.assign(target, transitInsuranceRates);
        } else {
          // Otherwise we are editing, so determine the values from the business
          // and set it in the initial values.
          const mappedTransitInsuranceValue =
            mapBusinessValuesToInsuranceType(target) || transitInsuranceDefault;

          target.transitInsuranceApplicationDefault =
            mappedTransitInsuranceValue;
        }
      }
    },
    [
      billingSettings,
      business,
      defaultBuyerSplit,
      defaultVendorSplit,
      hasDefaultBuyerSplitsFeature,
      hasDefaultVendorSplitsFeature,
      hasInterestFeature,
      hasRelationshipsFeature,
      hasVendorCommissionsFeature,
      interestSettingsRaw,
      relationshipsRaw,
      tradingTermsLookup,
      vendorCommissionBandsRaw,
    ],
  );

  const initialValues = React.useMemo(() => {
    const initial = computeInitialValues(blankDefaults, context, business);

    applyDerivedValues(initial);

    return initial;
  }, [context, business, applyDerivedValues]);

  return initialValues;
};

const Form = (props: BusinessFormProps): React.JSX.Element => {
  const { closeSelf, business, defaultAddressSearch, handleSubmit, section } =
    props;

  const formikProps = useFormikContext<BusinessFormValues>();
  const {
    dirty,
    initialValues,
    isValid,
    resetForm,
    setFieldValue,
    values,
    touched,
  } = formikProps;
  const { abn: initialAbn } = initialValues;
  const { abn } = values;

  useBusinessFormSync(business, touched);

  const hasSyncAllPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureSyncAllAlternativeBusinessFields,
  );

  // Hide most of the contents of this page when editing a temporary business.
  // Clicking the "Convert Business" action will change the form to its normal state
  // and set the value in the form ready for pushing back to the server.
  const [isTemporary, setIsTemporary] = React.useState<boolean>(
    business?.isTemporary || false,
  );
  const setNotIsTemporary = (): void => {
    setIsTemporary(false);
    setFieldValue("isTemporary", false);
  };

  const doHandleClose = (): void => {
    // if (touched) {
    //   // TODO changes Dialog.
    // }
    closeSelf();
  };

  const [isSubFormActive, setIsSubFormActive] = React.useState<boolean>(false);

  // allow initial submit if fields are touched so that returning to the form
  // from another modal allows submission of touched fields.
  const hasTouchedFields = Object.values(touched).filter(Boolean).length > 0;

  const submitDisabled =
    !(dirty && hasTouchedFields && isValid) || isSubFormActive || isTemporary;

  // template string comparison to handle mixed types
  const abnDirty = dirty && `${initialAbn}` !== `${abn}`;

  const titleActionString: string = business
    ? `Edit ${getBusinessSubType(business)} "${business.name}"`
    : "Create Business";
  const submitTitle: string =
    abnDirty && !!abn ? "Submit & Query ABR" : "Submit";

  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);

  const parentObjectSelector = isLivestockAgent
    ? getActiveLivestockAgentDeployment
    : getActiveSaleyardAdminSaleyard;
  const parentObject = useSelector(parentObjectSelector) || {};

  const parentObjectId = parentObject.id;

  const auditLogLinkProps = useMemo(() => {
    if (isLivestockAgent) {
      return {
        auditLogType: AuditLogTypes.DEPLOYMENT_BUSINESS,
        queryParams: {
          deploymentId: parentObjectId,
        },
      };
    }
    if (isSaleyardAdmin) {
      return {
        auditLogType: AuditLogTypes.SALEYARD_BUSINESS,
        queryParams: {
          saleyardId: parentObjectId,
        },
      };
    }
    return {
      auditLogType: undefined,
    };
  }, [isLivestockAgent, isSaleyardAdmin, parentObjectId]);

  const saveAndClose = () => {
    if (!submitDisabled) {
      handleSubmit(values, touched);
    }
  };

  const saveAndKeepOpen = () => {
    if (!submitDisabled) {
      handleSubmit(values, touched, false);
      resetForm({
        values,
        touched: {},
      });
    }
  };

  return (
    <ZoomyDialog open onClose={doHandleClose} maxWidth="xl" fullWidth>
      <WaitForSync
        requiredData={[
          ApiModel.BUSINESSES,
          ApiModel.AGENCIES,
          ApiModel.TRADING_TERMS,
          ApiModel.VENDOR_COMMISSION_BANDS,
        ]}
      >
        <DialogTitle onClose={doHandleClose}>
          {business && (
            <AuditLogLink
              dataId={business.id}
              returnTo={window.location.hash}
              {...auditLogLinkProps}
            />
          )}
          &nbsp;
          {titleActionString}{" "}
          {hasSyncAllPermission && business && (
            <SyncButton
              to={`#${getSelectSyncDataHashRoute(
                business.id,
                window.location.hash,
              )}`}
              data-tour=""
            >
              <FontAwesomeIcon icon={faLink} /> Sync All
            </SyncButton>
          )}
          <AdminLink model="business" cbid={business?.id} />
        </DialogTitle>
        <DialogContent dividers form="true">
          {isTemporary ? (
            <TemporaryBusinessMessage setNotIsTemporary={setNotIsTemporary} />
          ) : (
            <DeploymentBusinessForm
              {...props}
              setIsSubFormActive={setIsSubFormActive}
              defaultAddressSearch={defaultAddressSearch}
              section={section}
            />
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={doHandleClose}>Cancel</SecondaryButton>
          {business ? (
            <MultiButton
              buttons={[
                {
                  title: "Save",
                  dataTour: "save",
                  isDisabled: submitDisabled,
                  onClick: saveAndKeepOpen,
                },
                {
                  title: "Save & Close",
                  dataTour: "submit",
                  default: true,
                  isDisabled: submitDisabled,
                  onClick: saveAndClose,
                },
              ]}
              flex
              buttonInline={false}
              stickySetting={Settings.businessFormSaveAction}
            />
          ) : (
            <Button
              data-tour="submit"
              onClick={saveAndClose}
              disabled={submitDisabled}
            >
              {submitTitle}
            </Button>
          )}
        </DialogActions>
      </WaitForSync>
    </ZoomyDialog>
  );
};

type ModalContext = ModalContextWithSnapshot<BusinessFormValues> & {
  businessId: string;
  section: string;
  defaultAddressSearch: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Formik snapshot from saga
  formikSnapshot: any;
  openSideLoadedBusinessModal?: boolean;
  sideLoadedBusinessId?: string;
};

const EditBusiness = ({ onClose }: EditBusinessProps): React.JSX.Element => {
  const context: ModalContext =
    useSelector(getContextByModalType(ModalTypes.EditBusiness)) || {};

  const {
    businessId,
    section,
    defaultAddressSearch,
    openSideLoadedBusinessModal,
    sideLoadedBusinessId,
  } = context;

  const dispatch = useDispatch();

  const editingBusiness = useSelector(
    getPropertyEnrichedBusinessByBusinessId(businessId),
  );
  const business = isEmpty(editingBusiness) ? undefined : editingBusiness;

  const handleSubmit = useCallback(
    (
      submittedValues: Partial<BusinessFormValues>,
      touched: FormikTouched<BusinessFormValues>,
      closeAfterSave = true,
    ) => {
      dispatch(submitBusinessForm(businessId, submittedValues, touched));
      if (closeAfterSave) {
        onClose();
      }
    },
    [dispatch, businessId, onClose],
  );

  const hasVendorCommissionsFeature = useHasDeploymentPermission(
    DeploymentPermission.featureVendorCommissions,
  );

  const hasDefaultBuyerSplitsFeature = useHasDefaultBuyerSplitPermission();
  const hasDefaultVendorSplitsFeature = useHasDefaultVendorSplitPermission();
  const hasRelationshipsFeature = useHasDeploymentPermission(
    DeploymentPermission.featureBusinessRelations,
  );

  // Memoised contexts to prevent formik enableReinitialization from wiping
  // user edits when switching sections
  const initialValuesContext = React.useMemo(
    () => ({
      values: context.values,
      formikSnapshot: context.formikSnapshot,
      businessId: context.businessId,
    }),
    [context.values, context.formikSnapshot, context.businessId],
  );

  const { formikSnapshot } = context;

  const initialValues = useBuildInitialValues(
    initialValuesContext,
    hasVendorCommissionsFeature,
    hasDefaultVendorSplitsFeature,
    hasDefaultBuyerSplitsFeature,
    hasRelationshipsFeature,
    business,
  );
  return (
    <Formik<Partial<BusinessFormValues>>
      initialValues={initialValues}
      validationSchema={getBusinessValidationSchema({
        includeVendorCommissionBands: hasVendorCommissionsFeature,
        includeDefaultBuyerSplit: hasDefaultBuyerSplitsFeature,
        includeDefaultVendorSplit: hasDefaultVendorSplitsFeature,
      })}
      initialTouched={formikSnapshot?.[context?.values?.snapshotId]?.touched}
      // No-op, but required for formik.  We handle the submission through onclick.
      onSubmit={noop}
      // enableReinitialize is intentionally omitted. useBuildInitialValues pulls from
      // live Redux selectors so any Pusher update would trigger a full form reset,
      // wiping user edits. Instead, the useEffect in Form syncs untouched fields
      // individually when the business changes in the store.
    >
      <Form
        closeSelf={onClose}
        business={business}
        section={section}
        defaultAddressSearch={defaultAddressSearch}
        handleSubmit={handleSubmit}
        openSideLoadedBusinessModal={openSideLoadedBusinessModal}
        sideLoadedBusinessId={sideLoadedBusinessId}
      />
    </Formik>
  );
};

export default EditBusiness;
